import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ArrowRight } from 'lucide-react';
import searchEntries from '@/data/searchIndex';

const RED = '#E02D45';
const categoryOrder = ['Mushin Factory OS', 'Mushin Vendor Connect', 'Product', 'Industry', 'Page', 'Manufacturing 4.0', 'Capability'];

/* ─── Scoring ─── */

function matchExact(keywords, q) {
  for (const kw of keywords) { if (kw === q) return true; }
  return false;
}

function matchPrefix(keywords, q) {
  for (const kw of keywords) { if (kw.startsWith(q)) return true; }
  return false;
}

function matchSubstring(keywords, q) {
  for (const kw of keywords) { if (kw.includes(q)) return true; }
  return false;
}

function scoreEntry(entry, query) {
  const q = query.toLowerCase().trim();
  if (!q) return 0;
  const name = entry.name.toLowerCase();
  const desc = entry.desc.toLowerCase();

  if (name === q) return 100;
  if (name.startsWith(q)) return 90;
  if (name.includes(q)) return 80;
  if (matchExact(entry.keywords, q)) return 75;
  if (matchPrefix(entry.keywords, q)) return 65;
  if (matchSubstring(entry.keywords, q)) return 55;

  const tokens = q.split(/\s+/);
  const searchable = [name, ...entry.keywords, desc].join(' ');
  const matched = tokens.filter(t =>
    new RegExp(`\\b${t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'i').test(searchable)
  );
  if (matched.length === tokens.length) return 45;
  if (matched.length > 0) return 20 + (matched.length / tokens.length) * 20;
  return 0;
}

function groupByCategory(results) {
  const groups = {};
  for (const r of results) {
    if (!groups[r.category]) groups[r.category] = [];
    groups[r.category].push(r);
  }
  return categoryOrder
    .filter(cat => groups[cat])
    .map(cat => ({ category: cat, items: groups[cat] }));
}

/* ─── Sub-components ─── */

function SearchInput({ inputRef, query, setQuery }) {
  return (
    <div className="flex items-center gap-3 px-5 border-b border-white/[0.06]">
      <Search className="w-[18px] h-[18px] text-white/25 flex-shrink-0" strokeWidth={2} />
      <input
        ref={inputRef}
        value={query}
        onChange={e => setQuery(e.target.value)}
        placeholder="Search apps, features, or solutions..."
        className="flex-1 bg-transparent text-[15px] text-white placeholder:text-white/25 font-medium py-4 outline-none"
        data-testid="search-input"
        autoComplete="off"
        spellCheck={false}
      />
      <kbd className="hidden sm:flex items-center gap-0.5 text-[11px] text-white/15 font-mono border border-white/[0.06] rounded-md px-1.5 py-0.5">
        ESC
      </kbd>
    </div>
  );
}

function PopularTags({ setQuery }) {
  const tags = ['Traceability', 'IoT', 'Quality', 'Calibration', 'CAPA', 'Supplier Audits', 'APQP', 'OEE', 'Paperless Factory', 'Training'];
  return (
    <div className="px-5 py-6">
      <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/15 mb-4">Popular searches</p>
      <div className="flex flex-wrap gap-2">
        {tags.map(tag => (
          <button
            key={tag}
            onClick={() => setQuery(tag)}
            className="text-[12px] font-semibold text-white/30 hover:text-white/60 bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.06] rounded-lg px-3 py-1.5 transition-colors"
            data-testid={`search-tag-${tag.toLowerCase().replace(/\s+/g, '-')}`}
          >
            {tag}
          </button>
        ))}
      </div>
    </div>
  );
}

function ResultItem({ item, isSelected, index, onSelect, onHover }) {
  const Icon = item.icon;
  return (
    <button
      data-idx={index}
      onClick={() => onSelect(item)}
      onMouseEnter={() => onHover(index)}
      className={`w-full flex items-center gap-3.5 px-5 py-2.5 text-left transition-colors ${
        isSelected ? 'bg-white/[0.05]' : 'hover:bg-white/[0.03]'
      }`}
      data-testid={`search-result-${index}`}
    >
      <div
        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 border"
        style={{
          backgroundColor: isSelected ? `${RED}15` : 'rgba(255,255,255,0.03)',
          borderColor: isSelected ? `${RED}30` : 'rgba(255,255,255,0.05)',
        }}
      >
        <Icon className="w-4 h-4" style={{ color: isSelected ? RED : 'rgba(255,255,255,0.3)' }} strokeWidth={1.7} />
      </div>
      <div className="flex-1 min-w-0">
        <span className={`text-[13px] font-bold truncate block ${isSelected ? 'text-white' : 'text-white/60'}`}>
          {item.name}
        </span>
        <p className="text-[11px] text-white/20 truncate mt-0.5">{item.desc}</p>
      </div>
      <ArrowRight
        className={`w-3.5 h-3.5 flex-shrink-0 transition-all ${
          isSelected ? 'opacity-60 translate-x-0' : 'opacity-0 -translate-x-1'
        }`}
        style={{ color: RED }}
        strokeWidth={2}
      />
    </button>
  );
}

function ResultsFooter({ count }) {
  return (
    <div className="px-5 py-2.5 border-t border-white/[0.04] flex items-center justify-between">
      <div className="flex items-center gap-4">
        <span className="flex items-center gap-1.5 text-[10px] text-white/15">
          <kbd className="border border-white/[0.08] rounded px-1 py-0.5 font-mono text-[9px]">&uarr;</kbd>
          <kbd className="border border-white/[0.08] rounded px-1 py-0.5 font-mono text-[9px]">&darr;</kbd>
          <span className="ml-0.5">navigate</span>
        </span>
        <span className="flex items-center gap-1.5 text-[10px] text-white/15">
          <kbd className="border border-white/[0.08] rounded px-1 py-0.5 font-mono text-[9px]">&crarr;</kbd>
          <span className="ml-0.5">select</span>
        </span>
      </div>
      <span className="text-[10px] text-white/10">{count} result{count !== 1 ? 's' : ''}</span>
    </div>
  );
}

/* ─── Main Dialog ─── */

export default function SearchDialog({ open, onClose }) {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const navigate = useNavigate();

  const results = useMemo(() => {
    if (!query.trim()) return [];
    return searchEntries
      .map(e => ({ ...e, score: scoreEntry(e, query) }))
      .filter(e => e.score > 0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 12);
  }, [query]);

  const grouped = useMemo(() => groupByCategory(results), [results]);
  const flatResults = useMemo(() => grouped.flatMap(g => g.items), [grouped]);

  const handleSelect = useCallback((entry) => {
    onClose();
    setQuery('');
    setSelectedIndex(0);

    const [basePath, hash] = entry.path.split('#');
    if (hash) {
      navigate(basePath);
      setTimeout(() => {
        document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 400);
    } else {
      navigate(entry.path);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [navigate, onClose]);

  // Focus input on open, reset on close
  useEffect(() => {
    if (open) {
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
    setQuery('');
    setSelectedIndex(0);
  }, [open]);

  // Reset selection on query change
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  // Keyboard navigation
  useEffect(() => {
    if (!open) return;
    function handleKey(e) {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(i => Math.min(i + 1, flatResults.length - 1));
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(i => Math.max(i - 1, 0));
      }
      if (e.key === 'Enter' && flatResults[selectedIndex]) {
        e.preventDefault();
        handleSelect(flatResults[selectedIndex]);
      }
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, flatResults, selectedIndex, handleSelect, onClose]);

  // Scroll selected item into view
  useEffect(() => {
    if (!listRef.current) return;
    const item = listRef.current.querySelector(`[data-idx="${selectedIndex}"]`);
    if (item) item.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  if (!open) return null;

  let flatIdx = -1;

  return (
    <div className="fixed inset-0 z-[100]" data-testid="search-overlay">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      <div className="relative flex justify-center pt-[12vh] sm:pt-[16vh] px-4">
        <div
          className="w-full max-w-[620px] bg-[#0C0C0C] border border-white/[0.08] rounded-2xl shadow-2xl shadow-black/60 overflow-hidden"
          data-testid="search-dialog"
          style={{ maxHeight: 'min(520px, 70vh)' }}
        >
          <SearchInput inputRef={inputRef} query={query} setQuery={setQuery} />

          <div ref={listRef} className="overflow-y-auto" style={{ maxHeight: 'min(420px, 58vh)' }}>
            {query.trim() && results.length === 0 && (
              <div className="px-5 py-10 text-center">
                <p className="text-sm text-white/25 font-medium">No results for "{query}"</p>
                <p className="text-xs text-white/15 mt-1.5">Try searching for an app, feature, or problem</p>
              </div>
            )}

            {!query.trim() && <PopularTags setQuery={setQuery} />}

            {grouped.map(group => (
              <div key={group.category}>
                <div className="px-5 pt-4 pb-1.5">
                  <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/15">{group.category}</p>
                </div>
                {group.items.map(item => {
                  flatIdx++;
                  return (
                    <ResultItem
                      key={`${item.name}-${item.path}`}
                      item={item}
                      isSelected={flatIdx === selectedIndex}
                      index={flatIdx}
                      onSelect={handleSelect}
                      onHover={setSelectedIndex}
                    />
                  );
                })}
              </div>
            ))}

            {results.length > 0 && <div className="h-3" />}
          </div>

          {results.length > 0 && <ResultsFooter count={results.length} />}
        </div>
      </div>
    </div>
  );
}
